import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import StatCard from '../../../components/common/StatCard';
import AlertToast from '../../../components/common/AlertToast';

// ─── Constants ─────────────────────────────────────────────────────────────────

const STATUSES = [
  { value: 'present',              label: 'Present',          color: 'success' },
  { value: 'absent_informed',      label: 'Absent (Informed)', color: 'warning' },
  { value: 'absent_not_informed',  label: 'Absent (Not Informed)', color: 'danger' },
];

const statusColor = (s) => STATUSES.find(x => x.value === s)?.color || 'secondary';
const statusLabel = (s) => STATUSES.find(x => x.value === s)?.label || s || '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const todayISO = () => new Date().toISOString().split('T')[0];

// ─── BSModal ────────────────────────────────────────────────────────────────────

const BSModal = ({ show, onClose, title, size = '', children, footer }) =>
  show ? (
    <div className="modal fade show d-block" style={{ background:'rgba(0,0,0,0.45)', zIndex:1050 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal-dialog modal-dialog-scrollable modal-dialog-centered ${size}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null;

// ─── Stats Dialog ───────────────────────────────────────────────────────────────

const StatsDialog = ({ show, batchId, batchNumber, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !batchId) return;
    setLoading(true);
    api.get(`/training/attendance/stats/${batchId}`)
      .then(r => setStats(r.data?.data || r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => setStats(null);
  }, [show, batchId]);

  const rows = stats?.stats || [];

  return (
    <BSModal show={show} onClose={onClose} title={`Attendance Summary — ${batchNumber || ''}`} size="modal-xl">
      {loading ? (
        <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="alert alert-info">No data available.</div>
      ) : (
        <>
          <p className="text-muted small mb-2">Total sessions: <strong>{stats?.totalSessions ?? 0}</strong></p>
          <div className="table-responsive">
            <table className="table table-sm table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th>Student</th>
                  <th className="text-center text-success">Present</th>
                  <th className="text-center text-warning">Absent (Informed)</th>
                  <th className="text-center text-danger">Absent (Not Informed)</th>
                  <th className="text-center">Attendance %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <div className="fw-semibold">{r.student?.firstName} {r.student?.lastName}</div>
                      <small className="text-muted">{r.student?.email}</small>
                    </td>
                    <td className="text-center">{r.present}</td>
                    <td className="text-center">{r.absent_informed ?? r.absentInformed ?? 0}</td>
                    <td className="text-center">{r.absent_not_informed ?? r.absentNotInformed ?? 0}</td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-2">
                        <div className="progress flex-grow-1" style={{ height: 8 }}>
                          <div
                            className={`progress-bar bg-${r.attendancePct >= 75 ? 'success' : r.attendancePct >= 50 ? 'warning' : 'danger'}`}
                            style={{ width: `${r.attendancePct}%` }}
                          />
                        </div>
                        <span className="small fw-bold">{r.attendancePct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </BSModal>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────────

export default function Attendance() {
  const { user } = useAuth();
  const role = user?.roleKey || user?.role || '';
  const canWrite = ['admin', 'manager', 'team_lead', 'faculty', 'teacher', 'trainer'].includes(role);
  const canAdmin = ['admin'].includes(role);
  const isStudent = role === 'student';
  const isParent  = role === 'parent';
  // Both students and parents are read-only — neither can mark or edit.
  const isViewOnly = isStudent || isParent;

  // Ref data
  const [batches, setBatches] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [batchRefreshKey, setBatchRefreshKey] = useState(0);
  const [batchStudentMap, setBatchStudentMap] = useState({}); // { [batchId]: [{firstName, lastName}] }
  const [selectedDate, setSelectedDate] = useState(todayISO());

  // Attendance data for the selected batch + date
  const [currentRecord, setCurrentRecord] = useState(null); // existing DB record or null
  const [enrolledStudents, setEnrolledStudents] = useState([]); // enrolled students list
  const [marks, setMarks] = useState({}); // { studentId: { status, note } }
  const [topic, setTopic] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');

  // List view (calendar)
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  // Students AND parents never see the Mark UI — backend would 403 on POST
  // anyway, but hiding the tab + landing on History keeps the experience
  // clean for read-only roles.
  const [activeTab, setActiveTab] = useState(isViewOnly ? 'history' : 'mark'); // 'mark' | 'history'

  // Stats
  const [showStats, setShowStats] = useState(false);

  // Saving
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);

  // Alert
  const [alert, setAlert] = useState({ show: false, msg: '', type: 'success' });
  const showAlert = (msg, type = 'success') => setAlert({ show: true, msg, type });

  // ── Load batches ────────────────────────────────────────────────────────────

useEffect(() => {
    setBatchLoading(true);
    const currentShowCompleted = isStudent ? false : showCompleted;
    const statusFilter = currentShowCompleted ? undefined : 'ongoing,started,upcoming';
    const params = { limit: 500 };
    if (statusFilter) params.status = statusFilter;
    console.log('[Attendance] Effect running, showCompleted=', currentShowCompleted, 'statusFilter=', statusFilter);
    api.get('/training/batches', { params })
      .then(r => {
        console.log('[Attendance] Full response:', JSON.stringify(r.data).slice(0, 200));
        let all = r.data?.records || r.data?.data || [];
        console.log('[Attendance] API returned', all.length, 'batches, currentShowCompleted=', currentShowCompleted, 'statuses:', all.map(b => b.status));
        // When showCompleted is false, explicitly filter to active statuses
        if (!currentShowCompleted) {
          all = all.filter(b => ['ongoing', 'started', 'upcoming'].includes(b.status));
        }
        console.log('[Attendance] After filter:', all.length, 'batches, about to setBatches with statuses:', all.map(b => b.status));
        setBatches(all);
        // Fetch enrolled students for all batches to show names in dropdown
        const studentMap = {};
        const fetchStudentNames = async (batchList) => {
          const activeStatuses = currentShowCompleted
            ? ['active', 'started', 'ongoing', 'completed']
            : ['active', 'started', 'ongoing'];
          await Promise.all(batchList.map(async (b) => {
            try {
              const r = await api.get('/training/enrollments', { params: { batch: b._id, limit: 200 } });
              const list = r.data?.data || r.data?.records || [];
              const active = list.filter(e => activeStatuses.includes(e.status));
              studentMap[b._id] = active
                .map(e => ({ firstName: e.student?.firstName, lastName: e.student?.lastName }))
                .filter(s => s.firstName);
            } catch (_) { studentMap[b._id] = []; }
          }));
          setBatchStudentMap({ ...studentMap });
        };
        if (all.length > 0) fetchStudentNames(all);
        // If selected batch is no longer in the list, reset to first available
        if (selectedBatch && !all.find(b => String(b._id) === String(selectedBatch))) {
          console.log('[Attendance] Resetting selectedBatch - not in list');
          setSelectedBatch(all.length > 0 ? all[0]._id : '');
        } else if (all.length > 0 && !selectedBatch) {
          console.log('[Attendance] Setting initial selectedBatch to', all[0]._id);
          setSelectedBatch(all[0]._id);
        } else if (all.find(b => String(b._id) === String(selectedBatch))) {
          console.log('[Attendance] Keeping selectedBatch:', selectedBatch);
        }
      })
      .catch(err => { console.error('[Attendance] API error:', err.message); })
      .finally(() => setBatchLoading(false));
  }, [batchRefreshKey, showCompleted, isStudent]);

  // Debug: log batches state changes
  useEffect(() => {
    console.log('[Attendance] batches state updated, count:', batches.length, 'statuses:', batches.map(b => b.status));
  }, [batches]);

  // ── Load enrolled students when batch changes ───────────────────────────────

  useEffect(() => {
    if (!selectedBatch || batchLoading) { setEnrolledStudents([]); return; }
    // Verify selected batch exists in current batches list
    if (!batches.find(b => String(b._id) === String(selectedBatch))) {
      setEnrolledStudents([]);
      return;
    }
    api.get('/training/enrollments', { params: { batch: selectedBatch, limit: 200 } })
      .then(r => {
        const list = r.data?.data || r.data?.records || [];
        // When showCompleted is true, show all enrollments including completed ones
        const activeStatuses = (isStudent ? false : showCompleted)
          ? ['active', 'started', 'ongoing', 'completed']
          : ['active', 'started', 'ongoing'];
        setEnrolledStudents(list.filter(e => activeStatuses.includes(e.status)));
      })
      .catch(() => {});
  }, [selectedBatch, showCompleted, batchLoading, isStudent]);

  // ── Load existing record for selected batch+date ────────────────────────────

  const loadRecord = useCallback(async () => {
    if (!selectedBatch || !selectedDate) return;
    try {
      const r = await api.get('/training/attendance', {
        params: { batchId: selectedBatch, startDate: selectedDate, endDate: selectedDate, limit: 1 },
      });
      const list = r.data?.records || r.data?.data || [];
      const rec = list[0] || null;
      setCurrentRecord(rec);
      setTopic(rec?.topic || '');
      setSessionNotes(rec?.notes || '');
      // Populate marks from existing record
      const m = {};
      if (rec?.attendanceRecords) {
        rec.attendanceRecords.forEach(x => {
          m[x.student?._id || x.student] = { status: x.status, note: x.note || '' };
        });
      }
      setMarks(m);
    } catch (_) {}
  }, [selectedBatch, selectedDate]);

  useEffect(() => { loadRecord(); }, [loadRecord]);

  // ── Load history records ────────────────────────────────────────────────────

  useEffect(() => {
    if (!selectedBatch || activeTab !== 'history') return;
    setLoadingRecords(true);
    api.get('/training/attendance', { params: { batchId: selectedBatch, limit: 60 } })
      .then(r => setRecords(r.data?.records || r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoadingRecords(false));
  }, [selectedBatch, activeTab]);

  // ── Mark all with same status ───────────────────────────────────────────────

  const markAll = (status) => {
    const m = {};
    enrolledStudents.forEach(e => {
      const sid = e.student?._id || e.student;
      if (sid) m[sid] = { status, note: marks[sid]?.note || '' };
    });
    setMarks(m);
  };

  const setStudentStatus = (sid, status) => {
    setMarks(p => ({ ...p, [sid]: { ...p[sid], status, note: p[sid]?.note || '' } }));
  };

  const setStudentNote = (sid, note) => {
    setMarks(p => ({ ...p, [sid]: { ...p[sid], note } }));
  };

  // ── Save attendance ──────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!selectedBatch) { showAlert('Select a batch.', 'danger'); return; }
    if (!selectedDate)  { showAlert('Select a date.',  'danger'); return; }
    if (currentRecord?.isLocked && !canAdmin) {
      showAlert('This record is locked.', 'danger'); return;
    }
    setSaving(true);
    try {
      const recordsPayload = enrolledStudents.map(e => {
        const sid = e.student?._id || e.student;
        return {
          student: sid,
          status:  marks[sid]?.status || 'absent_not_informed',
          note:    marks[sid]?.note   || '',
        };
      }).filter(x => x.student);

      const r = await api.post('/training/attendance', {
        batchId: selectedBatch,
        date: selectedDate,
        attendanceRecords: recordsPayload,
        topic,
        notes: sessionNotes,
      });

      if (r.data?.success !== false) {
        showAlert('Attendance saved.', 'success');
        setCurrentRecord(r.data?.data || r.data);
      } else {
        showAlert(r.data?.message || 'Failed to save.', 'danger');
      }
    } catch (e) {
      showAlert(e.response?.data?.message || 'Failed to save.', 'danger');
    }
    setSaving(false);
  };

  // ── Lock / Unlock ────────────────────────────────────────────────────────────

  const handleLock = async () => {
    if (!currentRecord?._id) { showAlert('Save attendance first before locking.', 'warning'); return; }
    setLocking(true);
    try {
      const r = await api.put(`/training/attendance/${currentRecord._id}/lock`, {});
      if (r.data?.success !== false) {
        setCurrentRecord(r.data?.data || r.data);
        showAlert('Attendance locked.', 'success');
      } else showAlert(r.data?.message || 'Failed to lock.', 'danger');
    } catch (e) { showAlert(e.response?.data?.message || 'Failed to lock.', 'danger'); }
    setLocking(false);
  };

  const handleUnlock = async () => {
    if (!currentRecord?._id) return;
    setLocking(true);
    try {
      const r = await api.put(`/training/attendance/${currentRecord._id}/unlock`, {});
      if (r.data?.success !== false) {
        setCurrentRecord(r.data?.data || r.data);
        showAlert('Attendance unlocked.', 'success');
      } else showAlert(r.data?.message || 'Failed to unlock.', 'danger');
    } catch (e) { showAlert(e.response?.data?.message || 'Failed to unlock.', 'danger'); }
    setLocking(false);
  };

  // ── Derived ──────────────────────────────────────────────────────────────────

  const presentCount         = Object.values(marks).filter(m => m?.status === 'present').length;
  const absentInformedCount  = Object.values(marks).filter(m => m?.status === 'absent_informed').length;
  const absentNotInfoCount   = Object.values(marks).filter(m => m?.status === 'absent_not_informed').length;
  const absentCount          = absentInformedCount + absentNotInfoCount;
  const totalStudents = enrolledStudents.length;
  const batch = batches.find(b => b._id === selectedBatch);
  const isLocked = currentRecord?.isLocked;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="container-fluid py-3">
      <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={() => setAlert(p => ({ ...p, show: false }))} />

      <PageHeader
        title="Attendance"
        icon="bi-calendar-check"
        subtitle="Mark and track batch attendance"
        actions={
          selectedBatch && (
            <button className="btn btn-outline-info btn-sm" onClick={() => setShowStats(true)}>
              <i className="bi bi-bar-chart me-1" />Summary
            </button>
          )
        }
      />

      {/* Batch + Date Selector */}
      <div className="row g-2 mb-3">
        <div className="col-md-5">
          <select
            className="form-select form-select-sm"
            value={selectedBatch}
            onChange={e => setSelectedBatch(e.target.value)}
          >
            <option value="">— Select Batch — ({batches.length} total)</option>
            {batches.map(b => {
              const students = batchStudentMap[b._id] || [];
              const preview = students.slice(0, 3).map(s => `${s.firstName}${s.lastName ? ' ' + s.lastName : ''}`).join(', ');
              const suffix = students.length > 3 ? ` (+${students.length - 3} more)` : students.length > 0 ? ` — ${preview}` : '';
              return (
                <option key={b._id} value={b._id}>
                  {b.batchNumber} — {b.course?.name || ''} [{b.status}]{suffix}
                </option>
              );
            })}
          </select>
        </div>
        <div className="col-md-3">
          <input
            className="form-control form-control-sm"
            type="date"
            value={selectedDate}
            max={todayISO()}
            onChange={e => setSelectedDate(e.target.value)}
          />
        </div>
        <div className="col-md-4 d-flex gap-2">
          <ul className="nav nav-tabs mb-0 border-0">
            {!isViewOnly && (
              <li className="nav-item">
                <button className={`nav-link py-1 ${activeTab === 'mark' ? 'active' : ''}`} onClick={() => setActiveTab('mark')}>
                  <i className="bi bi-pencil-square me-1" />Mark
                </button>
              </li>
            )}
            <li className="nav-item">
              <button className={`nav-link py-1 ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
                <i className="bi bi-clock-history me-1" />
                {isStudent ? 'My Attendance' : isParent ? "Child's Attendance" : 'History'}
              </button>
            </li>
          </ul>
        </div>
      </div>

      {!isStudent && (
      <div className="mb-3">
        <div className="form-check form-check-inline">
          <input className="form-check-input" type="checkbox" id="showCompleted" checked={showCompleted}
            onChange={e => { console.log('[Attendance] Checkbox clicked, e.target.checked:', e.target.checked); setShowCompleted(e.target.checked); setBatchRefreshKey(k => k + 1); console.log('[Attendance] After setShowCompleted, showCompleted state:', showCompleted); }} />
          <label className="form-check-label small" htmlFor="showCompleted">Show Completed Batches / Students</label>
        </div>
      </div>
      )}

      {/* ── MARK TAB ─────────────────────────────────────────────────────────── */}
      {activeTab === 'mark' && (
        <>
          {!selectedBatch ? (
            <div className="alert alert-info">Select a batch to mark attendance.</div>
          ) : enrolledStudents.length === 0 ? (
            <div className="alert alert-warning">No active enrollments found for this batch.</div>
          ) : (
            <>
              {/* Stats row */}
              {totalStudents > 0 && (
                <div className="row g-2 mb-3">
                  <div className="col-6 col-md-3">
                    <StatCard title="Total" value={totalStudents} icon="bi-people" color="primary" />
                  </div>
                  <div className="col-6 col-md-3">
                    <StatCard title="Present" value={presentCount} icon="bi-person-check" color="success" />
                  </div>
                  <div className="col-6 col-md-3">
                    <StatCard title="Absent (Informed)" value={absentInformedCount} icon="bi-person-dash" color="warning" />
                  </div>
                  <div className="col-6 col-md-3">
                    <StatCard title="Absent (Not Informed)" value={absentNotInfoCount} icon="bi-person-x" color="danger" />
                  </div>
                </div>
              )}

              {/* Lock banner */}
              {isLocked && (
                <div className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-3">
                  <i className="bi bi-lock-fill" />
                  <span>This attendance record is <strong>locked</strong>. Locked on {fmtDate(currentRecord?.lockedAt)}.</span>
                  {canAdmin && (
                    <button className="btn btn-sm btn-warning ms-auto" onClick={handleUnlock} disabled={locking}>
                      {locking ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-unlock me-1" />Unlock</>}
                    </button>
                  )}
                </div>
              )}

              {/* Topic + Notes */}
              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Topic covered today (optional)"
                    value={topic}
                    onChange={e => setTopic(e.target.value)}
                    disabled={isLocked && !canAdmin}
                  />
                </div>
                <div className="col-md-6">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Session notes (optional)"
                    value={sessionNotes}
                    onChange={e => setSessionNotes(e.target.value)}
                    disabled={isLocked && !canAdmin}
                  />
                </div>
              </div>

              {/* Bulk mark buttons */}
              {(!isLocked || canAdmin) && (
                <div className="d-flex flex-wrap gap-1 mb-3 align-items-center">
                  <span className="text-muted small me-1">Mark all:</span>
                  {STATUSES.map(s => (
                    <button key={s.value} className={`btn btn-outline-${s.color} btn-sm`} onClick={() => markAll(s.value)}>
                      {s.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Student list */}
              <div className="card">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Student</th>
                          <th>Status</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enrolledStudents.map((enr, idx) => {
                          const sid = enr.student?._id || enr.student;
                          const stu = enr.student || {};
                          const mark = marks[sid] || { status: 'absent', note: '' };
                          return (
                            <tr key={enr._id || sid}>
                              <td className="text-muted">{idx + 1}</td>
                              <td>
                                <div className="fw-semibold">{stu.firstName} {stu.lastName}</div>
                                <small className="text-muted">{stu.email}</small>
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  {STATUSES.map(s => (
                                    <button
                                      key={s.value}
                                      className={`btn btn-sm ${mark.status === s.value ? `btn-${s.color}` : `btn-outline-${s.color}`}`}
                                      onClick={() => setStudentStatus(sid, s.value)}
                                      disabled={isLocked && !canAdmin}
                                    >
                                      {s.label}
                                    </button>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <input
                                  className="form-control form-control-sm"
                                  style={{ minWidth: 120 }}
                                  placeholder="Note…"
                                  value={mark.note}
                                  onChange={e => setStudentNote(sid, e.target.value)}
                                  disabled={isLocked && !canAdmin}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Save + Lock buttons */}
              {canWrite && (!isLocked || canAdmin) && (
                <div className="d-flex gap-2 justify-content-end mt-3">
                  {currentRecord && !isLocked && (
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleLock} disabled={locking || saving}>
                      {locking ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-lock me-1" />Lock</>}
                    </button>
                  )}
                  <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                    {saving
                      ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
                      : <><i className="bi bi-check-lg me-1" />{currentRecord ? 'Update Attendance' : 'Save Attendance'}</>}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── HISTORY TAB ──────────────────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <>
          {!selectedBatch ? (
            <div className="alert alert-info">Select a batch to view history.</div>
          ) : loadingRecords ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : records.length === 0 ? (
            <div className="alert alert-info">No attendance records yet.</div>
          ) : (
            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Topic</th>
                        <th className="text-center text-success" title="Present">P</th>
                        <th className="text-center text-warning" title="Absent (Informed)">AI</th>
                        <th className="text-center text-danger" title="Absent (Not Informed)">AN</th>
                        <th className="text-center text-info" title="On Leave">L</th>
                        {!isStudent && <th>Status</th>}
                        {!isStudent && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {records.map(rec => {
                        // Schema stores the array as `attendanceRecords`. Earlier code
                        // read `rec.records` which was always undefined → filter() crashed.
                        const items = Array.isArray(rec.attendanceRecords) ? rec.attendanceRecords : [];
                        const count = (status) => items.filter(r => r.status === status).length;
                        const present  = rec.totalPresent ?? count('present');
                        const absentI  = rec.totalAbsentInformed ?? count('absent_informed');
                        const absentN  = rec.totalAbsentNotInformed ?? (count('absent_not_informed') + count('absent'));
                        const onLeave  = rec.leaveFromAnnouncement ? 1 : count('leave');
                        return (
                          <tr key={rec._id}>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <strong>{fmtDate(rec.date)}</strong>
                              <div className="text-muted small">{new Date(rec.date).toLocaleDateString('en-IN', { weekday: 'short' })}</div>
                            </td>
                            <td><small>{rec.topic || '—'}</small></td>
                            <td className="text-center fw-bold text-success">{present}</td>
                            <td className="text-center fw-bold text-warning">{absentI}</td>
                            <td className="text-center fw-bold text-danger">{absentN}</td>
                            <td className="text-center fw-bold text-info">{onLeave}</td>
                            {!isStudent && <td>
                              {rec.isLocked ? (
                                <span className="badge bg-secondary"><i className="bi bi-lock me-1" />Locked</span>
                              ) : (
                                <span className="badge bg-success-subtle text-success border border-success-subtle">Open</span>
                              )}
                            </td>}
                            {!isStudent && <td>
                              {canWrite && (
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  title="Edit this day"
                                  onClick={() => {
                                    setSelectedDate(new Date(rec.date).toISOString().split('T')[0]);
                                    setActiveTab('mark');
                                  }}
                                >
                                  <i className="bi bi-pencil" />
                                </button>
                              )}
                            </td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Stats Dialog */}
      <StatsDialog
        show={showStats}
        batchId={selectedBatch}
        batchNumber={batch?.batchNumber}
        onClose={() => setShowStats(false)}
      />
    </div>
  );
}
